"use client";

/**
 * SocialsMock — animated mock of the askFinz Socials workspace for the
 * /apps/socials marketing page.
 *
 * Shows the three-column layout: threads on the left, feed in the centre,
 * short-form on the right. The script types a new post into the composer,
 * publishes it (it appears at the top of the feed), then a reaction lands
 * on the top post and a thread reply appears below it.
 *
 * Lives in askfinz.com only. No real social-network APIs, no auth.
 */
import { useEffect, useRef, useState } from "react";
import {
  Heart,
  MessageCircle,
  Repeat2,
  BarChart3,
  Bookmark,
  Send,
  Hash,
  Bell,
  Search,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

/* ────────── Data ────────── */

type Post = {
  id: string;
  author: string;
  handle: string;
  initials: string;
  time: string;
  content: string;
  likes: number;
  comments: number;
  shares: number;
  views: number;
  hasLiked?: boolean;
};

const INITIAL_POSTS: Post[] = [
  {
    id: "p1",
    author: "Maya Chen",
    handle: "@mayac",
    initials: "MC",
    time: "2m",
    content:
      "Just shipped the new map overlay system. 13 new layers including pollution, rail and ocean data. The isochrone layer is the one I'm most proud of — pull up any address and see exactly where you can reach in 15/30/45 min. 🗺️",
    likes: 38,
    comments: 7,
    shares: 4,
    views: 612,
  },
  {
    id: "p2",
    author: "Ravi Singh",
    handle: "@ravis",
    initials: "RS",
    time: "14m",
    content:
      "The burndown chart in Project finally reflects actual velocity instead of estimated story points. Small thing, but it means the chart is now something you can make a decision from.",
    likes: 21,
    comments: 3,
    shares: 2,
    views: 334,
  },
  {
    id: "p3",
    author: "Lena Park",
    handle: "@lenapk",
    initials: "LP",
    time: "1h",
    content:
      "Hot take: the best feature in any tool is the one that makes you forget you're using a tool. askFinz Research gets there with the citation side-panel — you never lose track of where a claim came from.",
    likes: 64,
    comments: 12,
    shares: 9,
    views: 1840,
  },
];

const NEW_POST: Post = {
  id: "p0",
  author: "You",
  handle: "@you",
  initials: "YU",
  time: "now",
  content: "First time using the Socials workspace. The three-column layout is exactly right — threads on the left so the conversation is always visible. #askFinz",
  likes: 0,
  comments: 0,
  shares: 0,
  views: 1,
};

const COMPOSER_TEXT =
  "First time using the Socials workspace. The three-column layout is exactly right — threads on the left so the conversation is always visible. #askFinz";

const THREAD_REPLY = {
  author: "Maya Chen",
  initials: "MC",
  text: "Welcome! The thread column becomes indispensable once you're tracking a few conversations in parallel.",
};

const SIDE_TOPICS = ["#product", "#design", "#askFinz", "#devlog", "#maps"];

/* ────────── Component ────────── */

export function SocialsMock() {
  const [posts, setPosts] = useState<Post[]>(INITIAL_POSTS);
  const [composerText, setComposerText] = useState("");
  const [composerDone, setComposerDone] = useState(false);
  const [published, setPublished] = useState(false);
  const [likedId, setLikedId] = useState<string | null>(null);
  const [showReply, setShowReply] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers: number[] = [];
    const s = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(fn, ms));
    };

    const runCycle = () => {
      // Reset
      setPosts(INITIAL_POSTS);
      setComposerText("");
      setComposerDone(false);
      setPublished(false);
      setLikedId(null);
      setShowReply(false);

      // 1. Type the composer
      const total = COMPOSER_TEXT.length;
      const frames = Math.ceil(total / 5);
      for (let f = 1; f <= frames; f++) {
        s(() => setComposerText(COMPOSER_TEXT.slice(0, f * 5)), 400 + f * 28);
      }
      const typeEnd = 400 + frames * 28 + 200;
      s(() => { setComposerText(COMPOSER_TEXT); setComposerDone(true); }, typeEnd);

      // 2. "Publish" — post appears at top of feed
      s(() => {
        setPublished(true);
        setPosts([NEW_POST, ...INITIAL_POSTS]);
        setComposerText("");
        setComposerDone(false);
      }, typeEnd + 900);

      // 3. Someone likes post p1
      s(() => {
        setLikedId("p1");
        setPosts((prev) =>
          prev.map((p) => (p.id === "p1" ? { ...p, likes: p.likes + 1, hasLiked: true } : p))
        );
      }, typeEnd + 2200);

      // 4. Thread reply appears
      s(() => setShowReply(true), typeEnd + 3400);

      // 5. Clear like highlight
      s(() => setLikedId(null), typeEnd + 4200);

      // Loop
      s(runCycle, typeEnd + 7000);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            socials.askfinz.ai
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Live
          </span>
        </div>

        {/* Three-column body */}
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_160px] gap-px bg-foreground/[0.08] min-h-[480px]">

          {/* Left — threads */}
          <div className="hidden md:flex flex-col bg-background p-3">
            <div className="text-[9px] tracking-[0.2em] uppercase text-foreground/40 font-mono mb-2 px-1">
              Threads
            </div>
            {/* Thread preview */}
            <div className="rounded-md border hairline bg-foreground/[0.025] p-2.5 mb-2">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="inline-flex h-5 w-5 rounded-full bg-foreground/20 text-[8px] font-mono items-center justify-center text-background">
                  MC
                </span>
                <span className="text-[10px] font-medium text-foreground/85">Maya Chen</span>
              </div>
              <p className="text-[10px] text-foreground/65 leading-snug">
                The isochrone layer is the feature I didn&apos;t know I needed…
              </p>
              <div className="mt-1.5 text-[9px] font-mono text-foreground/40">4 replies · 2m ago</div>
            </div>
            <div className="rounded-md border hairline bg-foreground/[0.025] p-2.5 mb-2">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="inline-flex h-5 w-5 rounded-full bg-foreground/20 text-[8px] font-mono items-center justify-center text-background">
                  LP
                </span>
                <span className="text-[10px] font-medium text-foreground/85">Lena Park</span>
              </div>
              <p className="text-[10px] text-foreground/65 leading-snug">
                Citation side-panel in Research is the one feature that…
              </p>
              <div className="mt-1.5 text-[9px] font-mono text-foreground/40">12 replies · 1h ago</div>
            </div>
            {showReply && (
              <div
                className="rounded-md border hairline bg-[hsl(var(--accent))]/5 border-[hsl(var(--accent))]/20 p-2.5 animate-[blur-fade-in_0.4s_ease-out]"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="inline-flex h-5 w-5 rounded-full bg-foreground/20 text-[8px] font-mono items-center justify-center text-background">
                    MC
                  </span>
                  <span className="text-[10px] font-medium text-foreground/85">Maya Chen</span>
                </div>
                <p className="text-[10px] text-foreground/75 leading-snug">{THREAD_REPLY.text}</p>
                <div className="mt-1.5 text-[9px] font-mono text-[hsl(var(--accent))]">new reply · now</div>
              </div>
            )}
          </div>

          {/* Centre — feed */}
          <div className="bg-background flex flex-col">
            {/* Composer */}
            <div className="border-b hairline p-3">
              <div className="flex gap-2.5">
                <span className="inline-flex h-7 w-7 rounded-full bg-foreground/15 text-[10px] font-mono items-center justify-center shrink-0">
                  YU
                </span>
                <div className="flex-1 min-w-0">
                  <div className="min-h-[36px] text-[12px] leading-relaxed text-foreground/85">
                    {composerText || (
                      <span className="text-foreground/30">What&apos;s happening?</span>
                    )}
                    {composerText && !composerDone && (
                      <span className="inline-block h-3 w-0.5 bg-foreground/55 align-middle ml-0.5 animate-pulse" />
                    )}
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[10px] font-mono text-foreground/35">
                      {composerText.length > 0 ? `${composerText.length}/280` : ""}
                    </span>
                    <button
                      type="button"
                      className={
                        "inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-medium transition " +
                        (composerDone
                          ? "bg-foreground text-background"
                          : "bg-foreground/10 text-foreground/35")
                      }
                    >
                      {composerDone && <CheckCircle2 className="h-3 w-3" />}
                      Post
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feed posts */}
            <div className="divide-y divide-foreground/[0.055] overflow-auto">
              {posts.map((post) => (
                <FeedPost
                  key={post.id}
                  post={post}
                  isNew={post.id === "p0" && published}
                  likeFlash={likedId === post.id}
                />
              ))}
            </div>
          </div>

          {/* Right — trending / short-form */}
          <div className="hidden md:flex flex-col bg-background p-3">
            <div className="text-[9px] tracking-[0.2em] uppercase text-foreground/40 font-mono mb-2 px-1">
              Trending
            </div>
            <ul className="space-y-1.5">
              {SIDE_TOPICS.map((t) => (
                <li key={t} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] hover:bg-foreground/[0.035] cursor-pointer">
                  <Hash className="h-3 w-3 text-foreground/40" />
                  <span className="text-foreground/75">{t.replace("#", "")}</span>
                </li>
              ))}
            </ul>

            <div className="mt-4 pt-3 border-t hairline">
              <div className="text-[9px] tracking-[0.2em] uppercase text-foreground/40 font-mono mb-2">
                Suggested
              </div>
              {["@alexj", "@sai_k", "@mwong"].map((h) => (
                <div key={h} className="flex items-center gap-2 py-1.5">
                  <span className="inline-flex h-6 w-6 rounded-full bg-foreground/15 text-[8px] font-mono items-center justify-center">
                    {h[1].toUpperCase()}
                  </span>
                  <span className="text-[11px] text-foreground/65 font-mono">{h}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t hairline bg-foreground/[0.02] px-5 py-2.5 flex items-center justify-between">
          <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            Chronological feed · real-time reactions
          </span>
          <span className="font-mono text-[10px] text-foreground/35">Updated just now</span>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function FeedPost({
  post, isNew, likeFlash,
}: {
  post: Post;
  isNew: boolean;
  likeFlash: boolean;
}) {
  return (
    <div
      className={
        "px-3 py-3 transition-all duration-500 " +
        (isNew ? "animate-[blur-fade-in_0.5s_ease-out] bg-[hsl(var(--accent))]/[0.04]" : "")
      }
    >
      <div className="flex gap-2.5">
        <span className="inline-flex h-7 w-7 rounded-full bg-foreground/15 text-[9px] font-mono items-center justify-center shrink-0 mt-0.5">
          {post.initials}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[12px] font-semibold text-foreground/90">{post.author}</span>
            {isNew && (
              <span className="text-[9px] font-mono text-[hsl(var(--accent))] animate-[blur-fade-in_0.4s_ease-out]">
                · just posted
              </span>
            )}
            <span className="text-[10px] font-mono text-foreground/35 ml-auto">{post.time}</span>
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-foreground/80">
            {post.content}
          </p>
          {/* Actions */}
          <div className="mt-2 flex items-center gap-3 text-foreground/40">
            <ActionBtn
              icon={Heart}
              count={post.likes}
              active={!!post.hasLiked || likeFlash}
              activeColor="text-red-400"
            />
            <ActionBtn icon={MessageCircle} count={post.comments} />
            <ActionBtn icon={Repeat2} count={post.shares} />
            <ActionBtn icon={BarChart3} count={post.views} />
            <Bookmark className="h-3.5 w-3.5 ml-auto hover:text-foreground/70 cursor-pointer transition" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({
  icon: Icon, count, active, activeColor,
}: {
  icon: typeof Heart;
  count: number;
  active?: boolean;
  activeColor?: string;
}) {
  return (
    <button
      type="button"
      className={
        "inline-flex items-center gap-1 text-[10px] font-mono transition " +
        (active ? (activeColor ?? "text-foreground/80") : "hover:text-foreground/65")
      }
    >
      <Icon className="h-3.5 w-3.5" style={{ transition: "all 300ms ease" }} />
      <span style={{ transition: "all 300ms ease" }}>{count}</span>
    </button>
  );
}
